import { useQuery } from "@apollo/client";
import {
  IonAvatar,
  IonCard,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonImg,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonRow,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { ListSkeleton } from "components/Skeleton/ListSkeleton";
import { AuthContext } from "contexts/AuthContext";
import { GET_MERCHANT_TRANSACTIONS } from "graphql/queries";
import { useContext, useEffect, useMemo, useState } from "react";
import { TransactionStore } from "stores/TransactionStore";
import currencyFormat, { fiatFormat } from "utils/Currency";
import { thumbnail } from "utils/Utils";
import "./Transactions.css";

const Transactions = () => {
  const { authState } = useContext(AuthContext);
  const transactions = TransactionStore.useState((s) => s.transactions);
  const cursor = TransactionStore.useState((s) => s.cursor);
  const [numNewItems, setNumNewItems] = useState(20);
  const [fetchCursor, setFetchCursor] = useState(cursor);
  const { data, refetch, loading } = useQuery(GET_MERCHANT_TRANSACTIONS, {
    variables: { actorid: authState.user.uid, cursor: fetchCursor, limit: 20 },
  });
  const [loaded, setLoaded] = useState(false);

  const _newTransactions = useMemo(() => transactions, [transactions]);

  const newTransactions = useMemo(
    () => _newTransactions.slice().slice(0, numNewItems),
    [_newTransactions, numNewItems]
  );

  useEffect(() => {
    if (data?.activities?.length > 0) {
      setLoaded(true);
      TransactionStore.update((s) => {
        s.transactions = [...s.transactions, ...data.activities];
        s.cursor = data?.activities[data?.activities.length - 1].created_at;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const revenueValue = useMemo(
    () =>
      data?.activities_aggregate?.aggregate?.sum?.amount
        ? data?.activities_aggregate?.aggregate?.sum?.amount
        : 0,
    [data]
  );

  const fiatValue = useMemo(
    () =>
      data?.activities_aggregate?.aggregate?.sum?.fiat_amount
        ? data?.activities_aggregate?.aggregate?.sum?.fiat_amount
        : 0,
    [data]
  );

  const doRefresh = (event) => {
    event.detail.complete();
  };

  return (
    <IonPage className={"transaction-page-root"}>
      <IonHeader className={"ion-no-border"}>
        <IonToolbar className={"background-color"}>
          <IonTitle>Transactions</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonCard slot={"fixed"} className={"ion-no-margin stats-card"}>
          <IonGrid className={"stats-grid"} style={{ position: "sticky" }}>
            <IonRow>
              <IonCol>
                <IonLabel>
                  <h2>{currencyFormat(revenueValue)}</h2>
                  <p>Sats Revenue</p>
                </IonLabel>
              </IonCol>
              <IonCol>
                <IonLabel>
                  <h2>{fiatFormat(fiatValue)}</h2>
                  <p>USD Revenue</p>
                </IonLabel>
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonCard>
        <IonRefresher slot="fixed" onIonRefresh={doRefresh}>
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>
        {loading && !loaded && <ListSkeleton rows={20} />}
        <IonList lines="none" className={"wallet-list"}>
          {newTransactions.map((transaction, i) => {
            return (
              <IonItem
                button
                detail={false}
                className={`wallet-item`}
                key={"transaction-" + transaction.id + i}
              >
                <IonAvatar slot="start">
                  <IonImg
                    src={thumbnail(
                      transaction.purchase?.product?.media_url ||
                        transaction.purchase?.promotion?.product?.media_url,
                      100,
                      100
                    )}
                    alt={transaction.merchant?.business_name}
                  />
                </IonAvatar>
                <IonLabel className={"ion-text-wrap"}>
                  <h2>
                    {transaction.purchase?.product?.title ||
                      transaction.purchase?.promotion?.product?.title ||
                      "Multiple Products"}
                  </h2>
                  <p>{transaction.amount} sats</p>
                </IonLabel>
                <IonNote slot="end">
                  <IonLabel className={"ion-text-wrap ion-text-right"}>
                    <div className={"sub-title"}>
                      {fiatFormat(transaction.fiat_amount)}
                    </div>
                    <p>{new Date(transaction.created_at).toDateString()}</p>
                  </IonLabel>
                </IonNote>
              </IonItem>
            );
          })}
        </IonList>
        <IonInfiniteScroll
          onIonInfinite={async (event) => {
            if (numNewItems > _newTransactions.length) {
              return event.target.complete();
            }
            setFetchCursor(cursor);
            await refetch();

            const numItems = numNewItems + 20;
            setNumNewItems(numItems);
            event.target.complete();
          }}
          position={"bottom"}
          threshold={"100px"}
        >
          <IonInfiniteScrollContent
            loadingSpinner={"bubbles"}
          ></IonInfiniteScrollContent>
        </IonInfiniteScroll>
      </IonContent>
    </IonPage>
  );
};

export default Transactions;
